import React, { FormEvent, useState, useEffect } from 'react';
import logo from './Assets/logo.svg';

import './ActivationPage.scss';
import { Input, Button, Loader, Checkbox, Select } from '@scuf/common';
import { IAPIPackage } from '../../commonUtils/APIContext';
import useFetch, { IUseFetch } from '../../commonUtils/useFetch';
import { useParams } from 'react-router-dom';
import { v4 as uuid4 } from 'uuid';
import { IUserActivationResponse } from '../onboardingModels';
import { AgreementModal } from './AgreementModal';
import i18next from '../../../i18next';
import { LanguageCodes } from '../../../locales/LanguageCodeObj';
import randExp from 'randexp';
import { EMPTY_GUID, InstallerRole } from '../PartnerManagement/SKULIst/SKUModels';

interface IActivationForm {
  firstName: IFormStringField;
  lastName: IFormStringField;
  phone: IFormStringField;
  password: IFormStringField;
  confirmPassword: IFormStringField;
  eulaAcceptedVersion: IFormStringField;
  privacyAcceptedVersion: IFormStringField;
}

interface IFormStringField {
  value: string | undefined;
  touched: boolean;
}

interface ActivationPageParams {
  email: string;
  otp: string;
  // thirdPartyExistingUser: string;
}

interface UserIDPMapping {
  DomainNames: Array<string>;
  ProviderType: string;
  ProviderName: string;
}

interface IActivationPageProps extends IAPIPackage {}

export const ActivationPage = (props: IActivationPageProps) => {
  const environmentVariables = (window as any).ENV;
  const hstName = window.location.href
  const languageOptions = [
    { value: LanguageCodes.enUS, text: 'English US (English-US)' },
    { value: LanguageCodes.enGB, text: 'English GB (English-GB)' },
    { value: LanguageCodes.daDK, text: 'Danish (Dansk)' },
    { value: LanguageCodes.frFR, text: 'French (Français)' },
    { value: LanguageCodes.itIT, text: 'Italian (Italiano)' },
    { value: LanguageCodes.es419, text: 'Spanish-Latin America (Español)'},
    { value: LanguageCodes.deDE, text: 'German (Deutsche)'},
    { value: LanguageCodes.esES, text: 'Spanish-Spain (Español)'},
  ];
  const [languageCode, setLanguageCode] = useState<string>(LanguageCodes.enUS);
  // One time password/code, put in URL by activation email.
  // To be sent with the form data in order to validate the form
  let { email, otp } = useParams<
    ActivationPageParams
  >();

  const createFetchRequest = () => {
    const reqBody = {
      email,
      code: otp
    };
    const postRequest = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqBody)
    };
    return postRequest;
  };

  const [
    otpValidation,
    isLoadingOTPValidation,
    errorOTPValidaiton,
    otpValidationBody
  ]: IUseFetch<IUserActivationResponse> = useFetch(
    `${props.baseURL}/api/v1/registered-users/validate-otp-commands/${uuid4()}`,
    fetch,
    undefined,
    true
  );

  const [
    sendActivationFormResponse,
    isLoadingActivationFormResponse,
    errorActivationFormResponse,
    sendActivationForm
  ]: IUseFetch<IUserActivationResponse> = useFetch(
    `${
      props.baseURL
    }/api/v1/registered-users/activate-user-commands/${uuid4()}`,
    fetch,
    undefined,
    true
  );

  const [displaySuccess, setDisplaySuccess] = useState(false);
  const [eulaDisable, setEulaDisable] = useState(true);
  const [eula, setEula] = useState(false);
  const [privacyDisable, setPrivacyDisable] = useState(true);
  const [privacy, setPrivacy] = useState(false);
  const [isEulaModalVisisble, setEulaModalVisibility] = useState(false);
  const [isPrivacyModalVisisble, setPrivacyModalVisibility] = useState(false);
  const [thirdPartyExistingUser, setThirdPartyExistingUser] = useState<boolean> (false);

  useEffect(() => {
    setThirdPartyExistingUser(isCustomIDP(email));
    otpValidationBody(createFetchRequest());
  }, [email, otp]);

  const isCustomIDP = (email: string): boolean => {
    let userIdpMapping: UserIDPMapping[] = environmentVariables.USER_IDP_MAPPING;
    const decodedEmail = decodeURIComponent(email);

    if (decodedEmail) {
      let mail = decodedEmail.split('@');
      if (mail.length > 1) {
        let domain = mail[mail.length - 1];

        // Check if domain is honeywell.com (Azure PingFederate SSO)
        if (domain.toLowerCase() === 'honeywell.com') {
          return true;
        }

        let idpMappingObj = userIdpMapping.find(element => element.DomainNames.includes(domain));
        if (idpMappingObj) {
          return true;
        }
      }
    }
    return false;
  };

  useEffect(() => {
    if (!errorActivationFormResponse && sendActivationFormResponse) {
      setDisplaySuccess(true);
      // authService.login();
    }
  }, [sendActivationFormResponse, errorActivationFormResponse]);

  const [formDataFields, setFormDataFields] = useState<IActivationForm>({
    firstName: { value: undefined, touched: false },
    lastName: { value: undefined, touched: false },
    phone: { value: undefined, touched: false },
    password: { value: undefined, touched: false },
    confirmPassword: { value: undefined, touched: false },
    eulaAcceptedVersion: { value: '', touched: false },
    privacyAcceptedVersion: { value: '', touched: false }
  });

  const isTouched = (field: IFormStringField) => {
    return field.touched;
  };

  const hasValue = (field: IFormStringField) => {
    return !!field.value;
  };

  const hasNoValueAndTouched = (field: IFormStringField) => {
    return !hasValue(field) && isTouched(field);
  };

  const requiredAndTouchedError = (showError: boolean) => {
    return showError ? 'Required' : undefined;
  };

  const newFormVal = (val: string) => {
    return { value: val, touched: true };
  };

  const setAllToTouched = () => {
    setFormDataFields(prevState => {
      return {
        firstName: { value: prevState.firstName.value, touched: true },
        lastName: { value: prevState.lastName.value, touched: true },
        phone: { value: prevState.phone.value, touched: true },
        password: { value: prevState.password.value, touched: true },
        confirmPassword: {
          value: prevState.confirmPassword.value,
          touched: true
        },
        eulaAcceptedVersion: {
          value: prevState.eulaAcceptedVersion.value,
          touched: true
        },
        privacyAcceptedVersion: {
          value: prevState.privacyAcceptedVersion.value,
          touched: true
        }
      };
    });
  };

  // 204 expected, no data other than *not* loading and *not* in error state.
  const checkOTPValid = () => {
      // DEV ONLY: bypass OTP check on localhost so the form is accessible without a real token
      if (window.location.hostname === 'localhost') {
        return true;
      }
    let errorValid = false;
    if (otpValidation !== undefined && otpValidation !== null) {
      errorValid = otpValidation.output.status === 'VALIDATION_SUCCESSFUL';
    } else {
      errorValid = !errorOTPValidaiton;
    }
    const otpValid = errorValid && !isLoadingOTPValidation;
    return otpValid;
  };

  const checkNameIsValid = (name: IFormStringField) => {
    const nameValue = name.value;
    if (nameValue && nameValue.length > 0) {
      if (nameValue.charAt(0) === ' ') {
        return false;
      }
      let patturnString = /^.{0,254}[^\s]$/;
      const patturn = new RegExp(patturnString);
      return nameValue.match(patturn);
    } else {
      // Name required
      return false;
    }
  };

  const checkNameHasValidChars = (name: IFormStringField) => {
    if (name.value && name.value.length > 0) {
      const patternString = /^(?!\s)[a-zA-Z0-9äöüÄÖÜß\s]{1,256}(?<!\s)$/;
      const pattern = new RegExp(patternString);
      return name.value.match(pattern);
    }
  };

  const firstNameError = (name: IFormStringField) => {
    if (isTouched(name) && !checkNameIsValid(name)) {
      return i18next.t('invalidName');
    } if (isTouched(name) && !checkNameHasValidChars(name)) {
      return i18next.t('invalidFirstName');
    } else {
      return requiredAndTouchedError(hasNoValueAndTouched(name));
    }
  };

  const lastNameError = (name: IFormStringField) => {
    if (isTouched(name) && !checkNameIsValid(name)) {
      return i18next.t('invalidName');
    } if (isTouched(name) && !checkNameHasValidChars(name)) {
      return i18next.t('invalidLastName');
    } else {
      return requiredAndTouchedError(hasNoValueAndTouched(name));
    }
  };

  const checkPhoneIsValid = (phoneNo: IFormStringField) => {
    if (phoneNo.value) {
      let patturnString = /[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s.[0-9]*$/;
      const patturn = new RegExp(patturnString);
      const isValid = !!phoneNo.value.match(patturn);
      return isValid;
    } else {
      // Phone optional
      return true;
    }
  };

  const phoneError = (phoneNo: IFormStringField) => {
    if (!checkPhoneIsValid(phoneNo)) {
      return i18next.t('invalidPhoneNumber');
    } else {
      return undefined;
    }
  };

  const checkPasswordsMatch = () => {
    return (
      formDataFields.password.value === formDataFields.confirmPassword.value
    );
  };

  const checkPasswordIsValid = (password: IFormStringField) => {
    if (password.value) {
      // Password must contain at least 1 upper case letter, 1 lower case letter, 1 digit and 1 special character
      let patturnString = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W)[A-Za-z\d\W]{11,255}$/;
      const patturn = new RegExp(patturnString);
      const isValid = !!password.value.match(patturn);
      return isValid;
    } else {
      // Password required
      return false;
    }
  };

  const newPasswordErrorString = () => {
    let errorMsg = '';
    if (isTouched(formDataFields.password)) {
      if (!checkPasswordIsValid(formDataFields.password)) {
        errorMsg = i18next.t('eligiblePwd');
      }
      if (!hasValue(formDataFields.password)) {
        errorMsg = i18next.t('required');
      }
    }

    return errorMsg;
  };

  const confirmPasswordErrorString = () => {
    let errorMsg = '';
    if (isTouched(formDataFields.confirmPassword)) {
      if (!checkPasswordsMatch()) {
        errorMsg = i18next.t('pwdMatch');
      }
      if (!hasValue(formDataFields.confirmPassword)) {
        errorMsg = i18next.t('required');
      }
    }

    return errorMsg;
  };

  const isEntireFormValid = () => {
    if (
      checkNameIsValid(formDataFields.firstName) &&
      checkNameHasValidChars(formDataFields.firstName) &&
      checkNameIsValid(formDataFields.lastName) &&
      checkNameHasValidChars(formDataFields.lastName) &&
      checkPhoneIsValid(formDataFields.phone) &&
      (thirdPartyExistingUser ||
        (checkPasswordIsValid(formDataFields.password) &&
          checkPasswordsMatch())) &&
      eula &&
      privacy
    ) {
      return true;
    } else {
      return false;
    }
  };

  const handleSubmitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData();
    if (otp && email) {
      formData.append(
        'firstName',
        formDataFields.firstName.value ? formDataFields.firstName.value : ''
      );
      formData.append(
        'lastName',
        formDataFields.lastName.value ? formDataFields.lastName.value : ''
      );
      formData.append(
        'phone',
        formDataFields.phone.value ? formDataFields.phone.value : ''
      );
      if (thirdPartyExistingUser) {
        // temporary code
        const randPass = new randExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W)[A-Za-z\d\W]{11,255}$/).gen();
        formData.append('password',randPass);
        formData.append('confirmPassword',randPass);
      } else {
        formData.append(
          'password',
          formDataFields.password.value ? formDataFields.password.value : ''
        );
        formData.append(
          'confirmPassword',
          formDataFields.confirmPassword.value
            ? formDataFields.confirmPassword.value
            : ''
        );
      }
      formData.append(
        'eulaAcceptedVersion',
        formDataFields.eulaAcceptedVersion.value
          ? formDataFields.eulaAcceptedVersion.value
          : ''
      );
      formData.append(
        'dataPrivacyAcceptedVersion',
        formDataFields.privacyAcceptedVersion.value
          ? formDataFields.privacyAcceptedVersion.value
          : ''
      );
      // As the email comes through the URL, needs to be string decoded in order to pass correct string to API
      formData.append('email', decodeURIComponent(email));
      formData.append('oneTimePassword', otp);
    }

    if (isEntireFormValid()) {
      sendActivationForm({
        method: 'PUT',
        body: formData
      });
    }
  };

  const onCloseEULAModal = () => {
    setEulaDisable(false);
    setEulaModalVisibility(true);
  };

  const onClosePrivacyModal = () => {
    setPrivacyDisable(false);
    setPrivacyModalVisibility(true);
  };

  const changeLanguage = (languageCode: string) => {
    let translation = require(`../../../locales/${languageCode}/translations`)
      .translations[languageCode].translation;
    i18next.addResources(languageCode, 'translation', translation);
    i18next.loadResources();
    i18next.changeLanguage(languageCode);
    i18next.loadLanguages(languageCode);
    setLanguageCode(languageCode);
  };

  const getLoginURL = (isBMSAssistEnabled?: boolean) => {
    let loginURL = environmentVariables.BASE_URL;

    if (isBMSAssistEnabled !== false) {
      const dataCentre = localStorage.getItem('selectedDataCentre');
      if (dataCentre === 'ae') {
        loginURL = `${environmentVariables.BASE_URL_AE}/app/feature/home`;
      } else if (dataCentre === 'eu') {
        loginURL = `${environmentVariables.BASE_URL_EU}/app/feature/home`;
      } else {
        loginURL = `${environmentVariables.BASE_URL_US}/app/feature/home`;
      }
    }

    return (
      <a href={loginURL}>
        <Button className="signin-btn">{i18next.t('signin')}</Button>
      </a>
    );
  }

  return (
    <main className="activation-page-form-container">
      <section className="headings">
        <Select
          className="language-selector"
          options={languageOptions}
          value={languageCode}
          onChange={value => changeLanguage(value)}
        />
        <div className="logo-container">
          <img src={logo} alt="Honeywell" className="honeywell-title" />
          <span className="vertical-divider" />
          <div className="smartview-text">Connected Buildings</div>
        </div>
      </section>
      {isEulaModalVisisble && (
        <AgreementModal
          languageCode={languageCode}
          title={i18next.t('endUser')}
          type="EULA"
          onClose={(version: string) => {
            setEulaModalVisibility(false);
            setFormDataFields(prevState => {
              return { ...prevState, eulaAcceptedVersion: newFormVal(version) };
            });
          }}
          authFetch={props.authFetch}
          baseURL={props.baseURL}
          offering={props.offering}
        />
      )}
      {isPrivacyModalVisisble && (
        <AgreementModal
          languageCode={languageCode}
          title={`Honeywell ${i18next.t('privacyPolicy')}`}
          type="Privacy"
          onClose={(version: string) => {
            setPrivacyModalVisibility(false);
            setFormDataFields(prevState => {
              return {
                ...prevState,
                privacyAcceptedVersion: newFormVal(version)
              };
            });
          }}
          authFetch={props.authFetch}
          baseURL={props.baseURL}
          offering={props.offering}
        />
      )}
      <div className="content">
        {displaySuccess && sendActivationFormResponse ? (
          <>
            <div className="page-content">
              <span className="activate-user-text">
                {i18next.t('activationSuccess')}
              </span>
              <p>{i18next.t('newAccAccess')}</p>
              <p>
              {
                sendActivationFormResponse &&
                sendActivationFormResponse == null &&
                i18next.t('contactSupport')
              }
              {
                sendActivationFormResponse &&
                sendActivationFormResponse !== null &&
                sendActivationFormResponse.output.response.email &&
                i18next.t('username')
              }
              {
              sendActivationFormResponse &&
              sendActivationFormResponse !== null &&
              sendActivationFormResponse.output.response.email
              }
              


              {
              sendActivationFormResponse &&
              sendActivationFormResponse !== null &&
              sendActivationFormResponse.output.response.email &&
              i18next.t('alertnteId')
              }
              {
              sendActivationFormResponse &&
              sendActivationFormResponse !== null &&
              sendActivationFormResponse.output.response.userName
              }
              </p>
              <p>{i18next.t('emailSent')}</p>

              { ((window as any).ENV.ADMIN_MCD_HOSTNAME !== '' ? hstName.includes((window as any).ENV.ADMIN_MCD_HOSTNAME) : false) ? 
               sendActivationFormResponse.output.response.roleId !== InstallerRole
               &&
               <a href={environmentVariables.MCD_Supervisor_Url}>
                 <Button className="signin-btn">{i18next.t('signin')}</Button>
               </a> :
                sendActivationFormResponse.output.response.roleId !== InstallerRole
                && 
                getLoginURL(sendActivationFormResponse.output.response.isBMSAssistEnabled)
              }
            </div>
          </>
        ) : (
          <>
            {checkOTPValid() ? (
              <div className="page-content">
                <span className="activate-user-text">
                  {i18next.t('activateUserAcc')}
                </span>
                <form
                  id="activation-form"
                  className="activation-form"
                  method="post"
                  onSubmit={event => {
                    setAllToTouched();
                    handleSubmitForm(event);
                  }}
                >
                  <Input
                    hidden={true}
                    name="username"
                    type="text"
                    value={email ? decodeURIComponent(email) : ''}
                  />
                  <Input
                    name="firstName"
                    type="text"
                    label={i18next.t('firstName')}
                    autoComplete="given-name"
                    fluid={true}
                    indicator="required"
                    required={true}
                    reserveSpace={true}
                    error={firstNameError(formDataFields.firstName)}
                    onChange={val =>
                      setFormDataFields(prevState => {
                        return { ...prevState, firstName: newFormVal(val) };
                      })
                    }
                  />
                  <Input
                    name="lastName"
                    type="text"
                    label={i18next.t('lastName')}
                    autoComplete="family-name"
                    fluid={true}
                    required={true}
                    indicator="required"
                    reserveSpace={true}
                    error={lastNameError(formDataFields.lastName)}
                    onChange={val =>
                      setFormDataFields(prevState => {
                        return { ...prevState, lastName: newFormVal(val) };
                      })
                    }
                  />
                  <Input
                    name="phone"
                    type="tel"
                    pattern="[+]*[(]{0,1}[0-9]{1,3}[)]{0,1}[-\s\.[0-9]*$"
                    label={i18next.t('phoneNumber')}
                    autoComplete="tel"
                    fluid={true}
                    indicator={i18next.t('optional')}
                    reserveSpace={true}
                    error={phoneError(formDataFields.phone)}
                    onChange={val =>
                      setFormDataFields(prevState => {
                        return { ...prevState, phone: newFormVal(val) };
                      })
                    }
                  />
                  {!thirdPartyExistingUser ? (
                    <>
                      {' '}
                      <Input
                        name="password"
                        type="password"
                        label={i18next.t('password')}
                        autoComplete="password"
                        fluid={true}
                        required={true}
                        indicator="required"
                        reserveSpace={true}
                        minLength={11}
                        error={newPasswordErrorString()}
                        onChange={val =>
                          setFormDataFields(prevState => {
                            return { ...prevState, password: newFormVal(val) };
                          })
                        }
                      />
                      <Input
                        name="confirmPassword"
                        type="password"
                        label={i18next.t('confirmPassword')}
                        autoComplete="new-password"
                        fluid={true}
                        required={true}
                        indicator="required"
                        reserveSpace={true}
                        minLength={11}
                        error={confirmPasswordErrorString()}
                        onChange={val =>
                          setFormDataFields(prevState => {
                            return {
                              ...prevState,
                              confirmPassword: newFormVal(val)
                            };
                          })
                        }
                      />{' '}
                    </>
                  ) : null}

                  <div className="agreement-grid">
                    <Checkbox
                      disabled={eulaDisable}
                      className="grid-checkbox"
                      checked={eula}
                      onChange={checked => setEula(checked)}
                    />
                    <span
                      className={`grid-heading  ${
                        eulaDisable ? 'disabled' : ''
                      }`}
                    >
                      {i18next.t('iAccept')}
                    </span>
                    <span className="grid-heading">
                      {i18next.t('iHereby')}
                      <span className="link" onClick={onCloseEULAModal}>
                        {' '}
                        {i18next.t('eula')}
                      </span>
                    </span>
                  </div>
                  <div className="agreement-grid" style={{ marginTop: '7px' }}>
                    <Checkbox
                      disabled={privacyDisable}
                      className="grid-checkbox"
                      checked={privacy}
                      onChange={checked => setPrivacy(checked)}
                    />
                    <span
                      className={`grid-heading  ${
                        privacyDisable ? 'disabled' : ''
                      }`}
                    >
                      {i18next.t('iRead')}
                    </span>
                    <span className="grid-heading">
                      {i18next.t('iUnderstood')}
                      <span className="link" onClick={onClosePrivacyModal}>
                        {' '}
                        {i18next.t('privacyPolicy')}
                      </span>
                    </span>
                  </div>

                  {isLoadingActivationFormResponse ? (
                    <div className="default-loader-wrapper">
                      <Loader />
                    </div>
                  ) : (
                    <Button disabled={!isEntireFormValid()} actionType="submit">
                      {i18next.t('activate')}
                    </Button>
                  )}
                  {errorActivationFormResponse &&
                  errorActivationFormResponse.errorText ? (
                    <div className="error-box" style={{ color: 'red' }}>
                      {`${i18next.t('activationError')}: ${
                        errorActivationFormResponse.errorText
                      }. ${i18next.t('pleaseTryAgain')}`}
                    </div>
                  ) : null}
                </form>
              </div>
            ) : isLoadingOTPValidation ? (
              <div className="page-content default-loader-wrapper">
                <Loader />
              </div>
            ) : (
              <div className="page-content">
                <h1>{i18next.t('activationExpired')}</h1>
                <span>{i18next.t('activationExpiredText')}</span>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
};

export default ActivationPage;
 